import { ReloadOutlined } from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import { useSidebar, useTopHeader } from '_/contexts/LayoutContext';
import { Layout, Button, Tooltip, Spin, Typography, Dropdown, MenuProps } from 'antd';
import React, { ReactNode, ReactElement, useState, useMemo } from 'react';

import AppSidebar from './AppSidebar';

const { Header, Content } = Layout;
const { Title } = Typography;

export interface ActionButton {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  icon?: ReactNode;
  label?: string;
  disabled?: boolean;
  role?: string;
  tooltip?: string;
  dropdown?: MenuProps;
  className?: string;
}

export interface BreadcrumbItem {
  title: string;
  href?: string;
}

interface MainLayoutProps {
  children: ReactNode;
  isLoading?: boolean;
}

function MainLayout({ children, isLoading = false }: MainLayoutProps): ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);

  // Get layout state from contexts
  const { sidebarItems, sidebarContent } = useSidebar();
  const { breadcrumbs, actionButtons, onRefresh, refreshLoading, title, editableTitle, onTitleChange } = useTopHeader();

  // Derive title from last breadcrumb if not explicitly set
  const displayTitle = useMemo(() => {
    if (title !== null) return title;
    if (breadcrumbs.length > 0) {
      return breadcrumbs[breadcrumbs.length - 1].title;
    }
    return null;
  }, [title, breadcrumbs]);

  // Show breadcrumbs without the last item (since it's displayed as title)
  // Group breadcrumbs into pairs: [listLink, itemName]
  const breadcrumbPairs = useMemo(() => {
    if (breadcrumbs.length <= 1) return [];
    const items = breadcrumbs.slice(0, -1);
    const pairs: Array<{ listLink: BreadcrumbItem; itemName?: BreadcrumbItem }> = [];

    for (let i = 0; i < items.length; i += 2) {
      pairs.push({
        listLink: items[i],
        itemName: items[i + 1],
      });
    }
    return pairs;
  }, [breadcrumbs]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppSidebar collapsed={collapsed} onCollapse={setCollapsed} onWidthChange={setSidebarWidth} items={sidebarItems}>
        {sidebarContent}
      </AppSidebar>
      <Layout
        style={
          {
            'marginLeft': sidebarWidth,
            'transition': 'margin-left 0.2s ease',
            '--sidebar-width': `${sidebarWidth}px`,
          } as React.CSSProperties
        }
      >
        {(breadcrumbs.length > 0 || actionButtons.length > 0 || onRefresh || displayTitle) && (
          <Header
            style={{
              background: '#fff',
              padding: '0 24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 64,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '8px' }}>
              {breadcrumbPairs.map((pair, index) => (
                <React.Fragment key={index}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', maxWidth: 110 }}>
                    <Link
                      to={pair.listLink.href || '#'}
                      style={{
                        fontSize: '10px',
                        color: '#161323',
                        textDecoration: 'none',
                        lineHeight: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pair.listLink.title}
                    </Link>
                    {pair.itemName ? (
                      pair.itemName.href ? (
                        <Link
                          to={pair.itemName.href}
                          style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#262626',
                            textDecoration: 'none',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={pair.itemName.title}
                        >
                          {pair.itemName.title}
                        </Link>
                      ) : (
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#262626',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={pair.itemName.title}
                        >
                          {pair.itemName.title}
                        </span>
                      )
                    ) : (
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#8c8c8c', lineHeight: 1.2 }}>List</span>
                    )}
                  </div>
                  <span style={{ color: '#d9d9d9', fontSize: '12px', alignSelf: 'center' }}>/</span>
                </React.Fragment>
              ))}
              {displayTitle && (
                <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                  <Title
                    level={5}
                    editable={editableTitle && onTitleChange ? { onChange: onTitleChange } : false}
                    style={{ margin: 0, lineHeight: 1.2, fontSize: '14px' }}
                  >
                    {displayTitle}
                  </Title>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {onRefresh && (
                <Tooltip title="Refresh">
                  <Button
                    type="text"
                    shape="circle"
                    onClick={onRefresh}
                    loading={refreshLoading}
                    icon={!refreshLoading ? <ReloadOutlined /> : undefined}
                  />
                </Tooltip>
              )}
              {actionButtons.map((button, index) => {
                const buttonElement = (
                  <Button
                    key={index}
                    onClick={button.onClick}
                    disabled={button.disabled}
                    icon={button.icon}
                    className={button.className}
                  >
                    {button.label}
                  </Button>
                );

                // Wrap with Dropdown if dropdown menu is provided
                if (button.dropdown) {
                  return (
                    <Dropdown key={index} menu={button.dropdown} trigger={['click']}>
                      <Button
                        onClick={(e) => e.preventDefault()}
                        disabled={button.disabled}
                        icon={button.icon}
                        className={button.className}
                      >
                        {button.label}
                      </Button>
                    </Dropdown>
                  );
                }

                // Wrap with Tooltip if tooltip is provided
                if (button.tooltip) {
                  return (
                    <Tooltip key={index} title={button.tooltip}>
                      {buttonElement}
                    </Tooltip>
                  );
                }

                return buttonElement;
              })}
            </div>
          </Header>
        )}
        <Content
          style={{
            padding: '24px',
            background: '#f0f2f5',
            minHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', width: '100%' }}>
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                }}
              >
                <Spin size="large" />
              </div>
            ) : (
              children
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default MainLayout;
